<script setup>
import { computed, ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

import DsAlert from 'JIX/components/ds/DsAlert.vue';
import DsLinkBlock from 'JIX/components/ds/DsLinkBlock.vue';
import DsPagination from 'JIX/components/ds/DsPagination.vue';
import DsSortBy from 'JIX/components/ds/DsSortBy.vue';
import JobsearchAdmin from './JobsearchAdmin.vue';
import JobsearchDateRange from './JobsearchDateRange.vue';
import JobsearchResultListItem from './JobsearchResultListItem.vue';
import JobsearchTraveltimeModal from './JobsearchTraveltimeModal.vue';

import { getNumberFormatter } from 'JIX/utils.js';
import JobAdElementsManager from 'JIX/jobad/elements/ElementsManager.js';

import { mapParam } from 'JIX/jobsearch/utils.js';
import { __, __x, __nx } from 'JIX/gettext.js';
import JobsearchJobagentBanner from './JobsearchJobagentBanner.vue';
import JobsearchCategorySuggestionsAggressive from './JobsearchCategorySuggestionsAggressive.vue';
import JobsearchSuggestion from './JobsearchSuggestion.vue';
import JobsearchJobsForUkraineSuggestion from './JobsearchJobsForUkraineSuggestion.vue';

import SvgIcon from 'JIX/components/SvgIcon.vue';
import "icons/map-search.svg";
import { LoginResult, checkLogin } from 'JIX/utils/login.js';
import JobsearchCategorySuggestionsSimple from './JobsearchCategorySuggestionsSimple.vue';
import { getCookie, setCookie } from 'JIX/utils/cookies';

const store = useStore();

const JIX_NEW_JOBSEARCH_BEFORE_CONTINUE_COOKIE = 'jix-new-jobsearch-before-you-continue';
const NUM_JOBADS_BEFORE_BEFORE_YOU_CONTINUE = 2;
const seenBeforeYouContinue = getCookie(JIX_NEW_JOBSEARCH_BEFORE_CONTINUE_COOKIE) === '1';
const sessionid = window.Stash.common.sessionid;

const page = computed({
    get: () => store.state.searchQuery.page || 1,
    set: async (val) => {
        await store.dispatch('goToPage', val);
        header.value.scrollIntoView({ behavior: 'smooth' });
    },
});

const searchResponse = computed(() => store.state.searchResponse);

const isLimited = computed(() => {
    return searchResponse.value.total_pages > searchResponse.value.max_page;
});

const isAtMaxPage = computed(() => {
    return page.value === searchResponse.value.max_page && isLimited.value;
});

const hasCategorySuggestions = computed(() => {
    return !!searchResponse.value.suggestedChanges.category.suggestions?.length;
});

const AMOUNT_OF_CANDIDATES_TO_SHOW_BEFORE_YOU_CONTINUE_IN_PCT = 10;
const showAggressive = sessionid % AMOUNT_OF_CANDIDATES_TO_SHOW_BEFORE_YOU_CONTINUE_IN_PCT === 0;

const dismissCategorySuggestions = ref(false);
const showCategorySuggestionsAggressive = computed(() =>
    page.value === 1 &&
    hasCategorySuggestions.value &&
    !dismissCategorySuggestions.value &&
    !seenBeforeYouContinue &&
    showAggressive &&
    searchResponse.value.results.length > NUM_JOBADS_BEFORE_BEFORE_YOU_CONTINUE
);
const showCategorySuggestionsSimple = computed(() => hasCategorySuggestions.value && !showCategorySuggestionsAggressive.value);


function handleDismissCategorySuggestionsAggressive() {
    dismissCategorySuggestions.value = true;
    setCookie(JIX_NEW_JOBSEARCH_BEFORE_CONTINUE_COOKIE, 1, {
        path: '/',
        samesite: 'Lax',
        maxAge: 60 * 60 * 24 * 7,
    });
}

const numJobadsToShow = computed(() =>  showCategorySuggestionsAggressive.value ? NUM_JOBADS_BEFORE_BEFORE_YOU_CONTINUE : searchResponse.value.results.length);

const header = ref();

const traveltimeModal = ref();

const jobagentDefaultKey = `fallbackkey${crypto.randomUUID()}`;

const jobagentKey = computed(() => {
    return store.state.searchResponse.jobagent && store.state.searchResponse.jobagent !== null ? store.state.searchResponse.jobagent.id : jobagentDefaultKey;
});

const numberFormatter = getNumberFormatter();

const shownTids = computed(() => new Set(store.state.searchResponse.results.map(x => x.tid)));

const sort = mapParam(store, 'sort');
async function updateSort(value) {
    sort.value = value;
    return store.dispatch('performSearch');
}

const sortOptions = [
    { key: 'score', label: __('Relevans') },
    { key: 'date',   label: __('Nyeste') },
];

const hasJobageError = ref(false);

let observer;

onMounted(() => {
    const elementsManager = new JobAdElementsManager();

    window.addEventListener('reloadJobads', e => {
        elementsManager.handleReload(e.detail.jobadWrapper);
    });

    observer = new IntersectionObserver(handleIntersection, {
        root: null,
        threshold: 1,
    });

    watch(
        shownTids,
        () => {
            elementsManager.handleReload();
        },
        {
            deep: true,
            flush: 'post',
        },
    );

    watch(
        shownTids,
        () => {
            nextTick(() => {
                const items = document.querySelectorAll('.jobad-element-myjob');
                observer.disconnect();
                items.forEach(item => observer.observe(item));
            });
        },
        {
            deep: true,
            flush: 'post',
            immediate: true,
        },
    );

});

onBeforeUnmount(() => {
    if (observer) {
        observer.disconnect();
    }
});

function handleIntersection(entries) {
    for (const entry of entries) {
        if (entry.isIntersecting) {
            const tid = entry.target.dataset.tid;
            const result = store.state.searchResponse.results.find(r => r.tid === tid);
            if (result && result.html) {
                const match = result.html.match(/data-abtest_id=['"]([^'"]+)['"]/);
                if (match && match[1]) {
                    const abtestId = match[1];
                    // The element is now seen by the user
                    store.dispatch('abtestActivate', { abtestId });

                    // Find the element in the dom and set up a click listener
                    // to mark the abtest as successful when clicked
                    const element = document.querySelector(`[data-abtest_id="${abtestId}"]`);
                    element?.querySelector('.jobad-element-myjob')?.addEventListener('click', () => {
                        store.dispatch('abtestSuccess', { abtestId });
                    }, { once: true });
                }
                observer.unobserve(entry.target);


            }
        }
    }
}

async function toggleArchive() {
    store.commit('setSearchQuery', store.state.searchResponse.archive_toggle.params);
    return store.dispatch('performSearch');
}

const sitename = window.Stash.common.sitename;

async function showMapMode(){
    return store.dispatch('performSearch', { newViewMode: 'map' });
}

async function clickJobAd(event) {
    if (event.target.dataset.target === '#traveltime-modal' &&
        event.target.dataset.toggle === 'modal'){
        const res = await checkLogin();
        if (res !== LoginResult.ABORTED) {
            location.reload();
        }
    }
}
</script>

<template>
    <div>
        <JobsearchCategorySuggestionsSimple
            v-if="showCategorySuggestionsSimple"
            :category-suggestions="store.state.searchResponse.suggestedChanges.category"
        />
        <JobsearchSuggestion
            v-if="store.state.searchResponse.suggestedChanges.query.suggestion"
            :suggestion="store.state.searchResponse.suggestedChanges.query"
        />
        <JobsearchJobsForUkraineSuggestion
            v-if="store.state.searchResponse.suggestedChanges.jobsForUkraine"
        />
        <div
            v-if="store.state.searchResponse.suggestedChanges.company"
            v-html="store.state.searchResponse.suggestedChanges.company"
        />
        <h2 ref="header">
            {{ __nx('{n} jobannonce', '{n} jobannoncer', searchResponse.hitcount, { n: numberFormatter.format(searchResponse.hitcount) }) }}
        </h2>
        <JobsearchAdmin
            class="mb-3"
            v-if="$store.state.hasAdminAccess"
        />

        <JobsearchTraveltimeModal
            ref="traveltimeModal"
        />

        <DsAlert type="warning" v-if="store.getters.isArchive" class="mb-4">
            <form @submit.prevent="store.dispatch('performSearch')">
                <h5 class="mb-0">
                    {{ store.getters.isCombinedArchive ? __('Du ser nu alle job') : __('Du søger nu i arkivet') }}
                </h5>

                <p>{{ store.getters.isCombinedArchive ? __x('Du søger nu blandt alle annoncer på {sitename} gennem tiden. Vær opmærksom på, at flere af annoncerne ikke længere er aktuelle. Hvis du vil vide, om der stadig er åbent for ansøgninger, kan du klikke på annoncen eller kontakte virksomheden.', { sitename }) : __x('Her ser du jobannoncer, der ikke længere er aktuelle på {sitename}. Hvis du vil vide, om der stadig er åbent for ansøgninger, kan du klikke på annoncen eller kontakte virksomheden.', { sitename } ) }}</p>

                <JobsearchDateRange @has-error="hasJobageError = $event" />

                <div class="d-flex justify-content-between align-items-center gap-3 mt-4">
                    <a :href="store.state.searchResponse.archive_toggle.url" @click.prevent="toggleArchive">{{ __('Tilbage til aktuelle job') }}</a>
                    <button type="submit" class="btn btn-primary" :disabled="hasJobageError">
                        {{ __('Opdater') }}
                    </button>
                </div>
            </form>
        </DsAlert>

        <JobsearchJobagentBanner v-if="!store.getters.isArchive" :key="jobagentKey" />

        <div
            class="d-flex mb-1 align-items-center"
            :class="store.state.eligibleForNewJobsearchMap ? 'justify-content-between' : 'justify-content-end'"
        >
            <button
                type="button"
                :aria-label="__('Kortvisning')"
                class="btn btn-sm btn-tertiary gap-2"
                @click="showMapMode()"
                v-if="store.state.eligibleForNewJobsearchMap"
            >
                <SvgIcon name="map-search" size="xs" />
                {{ __("Kortvisning") }}
            </button>
            <DsSortBy
                id="jobsearch-sort"
                :criteria="sortOptions"
                :primary="sort || 'score'"
                @update:primary="updateSort"
            />
        </div>

        <JobsearchResultListItem
            :jobad="result"
            v-for="result, index in searchResponse.results.slice(0, numJobadsToShow)"
            :key="result.tid"
            @click="clickJobAd($event)"
            :fade-out-down="index + 1 === numJobadsToShow && showCategorySuggestionsAggressive"
        />
        <JobsearchCategorySuggestionsAggressive
            v-if="showCategorySuggestionsAggressive"
            :category-suggestions="store.state.searchResponse.suggestedChanges.category"
            @close="handleDismissCategorySuggestionsAggressive"
        />
        <div class="rounded bg-surface-1 border p-3" v-if="searchResponse.hitcount === 0 && !store.getters.isArchive && store.state.enableArchive">
            <h5>{{ __('Er jobbet forsvundet?') }}</h5>

            <p>{{ __('Hvis du ikke kan finde et specifikt job, er annoncen måske ikke længere aktiv. Men du kan altid finde den igen i vores arkiv!') }}</p>

            <DsLinkBlock :href="store.state.searchResponse.archive_toggle.url" @click.prevent="toggleArchive">
                {{ __('Gå til arkiv') }}
            </DsLinkBlock>
        </div>

        <DsAlert type="warning" v-if="isAtMaxPage" class="my-3">
            <p class="mb-0">
                <strong>{{ __("Vi kan desværre ikke vise flere jobannoncer") }}</strong><br>
                {{ __("Prøv at søge mere præcist.") }}
            </p>
        </DsAlert>

        <div class="d-flex justify-content-center" v-if="!showCategorySuggestionsAggressive">
            <DsPagination
                class="mt-4 mx-auto"
                :items="searchResponse.hitcount"
                :per-page="searchResponse.page_size"
                :base-url="searchResponse.link_canonical"
                v-model:page="page"
                v-if="searchResponse.hitcount > searchResponse.page_size"
                :max-page="searchResponse.max_page"
            />
        </div>
    </div>
</template>
